import { Icon, Stack, TableRow, Theme, Tooltip, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  AssessmentForReport,
  DATE_FORMAT_SHORT_FNS,
  EvaluationType,
  GetReportsQuerySort,
  Report,
  ReportStatuses,
  SORT_DIRECTION,
} from '@schooly/api';
import { DATE_FORMAT } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  ArrowAngleIcon,
  GridCell,
  GridHead,
  SortableCell,
  TruncatedLinks,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import useAppLocation from '../../hooks/useAppLocation';
import { getReportStatusTextId } from './utils';
type ReportsHeaderProps = {
  sort?: GetReportsQuerySort[];
  onChangeSort: (v: GetReportsQuerySort[]) => void;
};

export const ReportsHeader: FC<ReportsHeaderProps> = ({ onChangeSort, sort }) => {
  const { formatMessage } = useIntl();
  const handleSort = (columnTextId: GetReportsQuerySort['columnTextId']) => () => {
    const currentDirection = sort?.find((s) => s.columnTextId === columnTextId)?.direction;
    const newDirection =
      currentDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;

    switch (columnTextId) {
      case 'report_date':
        onChangeSort([
          { columnTextId: 'report_date', direction: newDirection },
          { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
        ]);
        break;
      case 'name':
        onChangeSort([
          { columnTextId: 'name', direction: newDirection },
          { columnTextId: 'report_date', direction: SORT_DIRECTION.DESC },
        ]);
        break;
      case 'report_status':
        onChangeSort([
          { columnTextId: 'report_status', direction: newDirection },
          { columnTextId: 'publish_date', direction: SORT_DIRECTION.DESC },
          { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'report_date', direction: SORT_DIRECTION.DESC },
        ]);
        break;
      default:
        onChangeSort([{ columnTextId, direction: newDirection }]);
    }
  };

  return (
    <GridHead
      borderBottom
      sx={{
        '& .MuiTableCell-root:first-of-type': {
          pl: 2.5,
        },
        '& .MuiTableCell-root:last-of-type': {
          pr: 2.5,
        },
      }}
    >
      <SortableCell
        label={formatMessage({ id: 'reports-Date' })}
        sort={sort?.[0]}
        columnTextId="report_date"
        onChangeSort={handleSort}
        width={80}
      />
      <SortableCell
        label={formatMessage({ id: 'reports-Name' })}
        sort={sort?.[0]}
        columnTextId="name"
        onChangeSort={handleSort}
      />
      <GridCell
        width={320}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 250,
          },
          [theme.breakpoints.only('md')]: {
            width: 170,
          },
          [theme.breakpoints.down('md')]: {
            width: 200,
          },
        })}
      >
        {formatMessage({ id: 'reports-AoLTutorsFeedback' })}
      </GridCell>
      <GridCell
        width={320}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 250,
          },
          [theme.breakpoints.only('md')]: {
            width: 170,
          },
          [theme.breakpoints.down('md')]: {
            width: 200,
          },
        })}
      >
        {formatMessage({ id: 'reports-AdditionalAssessments' })}
      </GridCell>
      <SortableCell
        label={formatMessage({ id: 'reports-Status' })}
        sort={sort?.[0]}
        columnTextId="report_status"
        onChangeSort={handleSort}
        width={170}
      />
    </GridHead>
  );
};

type ReportRowProps = {
  report: Report;
};

export const ReportRow: FC<ReportRowProps> = ({ report }) => {
  const location = useAppLocation();
  const { scheduled_publish_date } = report;
  const { formatMessage } = useIntl();

  const [evaluationCellAssessments, additionalAssessments] = useMemo(() => {
    return (
      report.assessments?.reduce<[AssessmentForReport[], AssessmentForReport[]]>(
        (acc, a) => {
          if (a.evaluation_type !== EvaluationType.Regular) {
            acc[0].push(a);
          }
          if (!a.autogenerated) {
            acc[1].push(a);
          }

          return acc;
        },
        [[], []],
      ) ?? [[], []]
    );
  }, [report.assessments]);

  return (
    <TableRow
      component={Link}
      onClick={() => {
        trackAmplitudeEvent({
          name: AnalyticsEventName.ReportOpen,
          payload: {
            report_name: report.name,
            report_date: report.scheduled_publish_date,
            status: formatMessage({
              id: getReportStatusTextId(report.report_status ?? ReportStatuses.Draft),
            }),
          },
        });
      }}
      to={`/reports/${report.id}`}
      state={{ backgroundLocation: location }}
      sx={(theme) => ({
        '&:hover td': {
          backgroundColor: theme.palette.background.default,
          '.MuiTypography-root': {
            '&:not(.noHoverText)': {
              color: theme.palette.primary.main,
            },
            '&.hoverTextSecondary': {
              color: theme.palette.common.grey2,
            },
          },
        },
      })}
    >
      <GridCell pl={2.5}>
        <Typography color="common.grey" className="hoverTextSecondary">
          {scheduled_publish_date
            ? format(
                newDateTimezoneOffset(scheduled_publish_date),
                DATE_FORMAT_SHORT_FNS,
              ).toUpperCase()
            : '—'}
        </Typography>
      </GridCell>
      <GridCell>
        <TypographyWithOverflowHint variant="h3" color="common.grey2" noWrap>
          {report.name}
        </TypographyWithOverflowHint>
      </GridCell>
      <GridCell>
        <EvaluationCellContent assessments={evaluationCellAssessments} />
      </GridCell>
      <GridCell>
        <AssessmentsCellContent assessments={additionalAssessments} />
      </GridCell>
      <GridCell pr={2.5}>
        <StatusCellContent report={report} />
      </GridCell>
    </TableRow>
  );
};

type StatusCellContentProps = {
  report: Report;
};

export const StatusCellContent: React.FC<StatusCellContentProps> = ({ report }) => {
  const { formatMessage } = useIntl();

  const isPublished = report.report_status === ReportStatuses.Published;
  const isDraft = report.report_status === ReportStatuses.Draft;

  const currentStatus = isPublished ? (
    <>
      {formatMessage({ id: 'status-Published' })}{' '}
      {report.actual_publish_date
        ? format(newDateTimezoneOffset(report.actual_publish_date), DATE_FORMAT)
        : ''}
    </>
  ) : (
    <> {formatMessage({ id: isDraft ? 'status-Draft' : 'status-NotPublished' })}</>
  );

  const getColor = () => {
    if (isPublished) return 'success.main';
    if (isDraft) return 'common.grey';
    return 'warning.main';
  };

  return (
    <Typography className={isDraft ? 'hoverTextSecondary' : 'noHoverText'} color={getColor()}>
      {currentStatus}
    </Typography>
  );
};

const COMMON_TOOLTIP_PROPS = {
  arrow: false,
  placement: 'bottom-start' as const,
  componentsProps: {
    tooltip: {
      sx: { px: 0.5, py: 0.25, minWidth: 300, maxHeight: 400, overflowY: 'scroll' },
    },
  },
  slotProps: {
    popper: {
      sx: (theme: Theme) => ({
        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
          margin: theme.spacing(-2.5, 0, 0, -0.5),
        },
      }),
    },
  },
};

type AssessmentsCellContentProps = {
  assessments: AssessmentForReport[];
};

export const AssessmentsCellContent: React.FC<AssessmentsCellContentProps> = ({ assessments }) => {
  if (!assessments.length) {
    return (
      <Typography color="common.grey" className="hoverTextSecondary">
        {'-'}
      </Typography>
    );
  }

  return (
    <TruncatedLinks
      items={assessments.map((a) => ({
        name: a.name,
        link: { to: `/assessments/${a.id}` },
      }))}
      typographyProps={{
        color: 'common.grey2',
        sx: { '&[href]:hover': { color: 'primary.main' } },
      }}
      tooltipProps={COMMON_TOOLTIP_PROPS}
    />
  );
};

export const EvaluationCellContent: React.FC<AssessmentsCellContentProps> = ({ assessments }) => {
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const assessmentsMap = useMemo(() => {
    return assessments.reduce<Record<string, AssessmentForReport[]>>((acc, a) => {
      let key = '';
      if (a.evaluation_type === EvaluationType.TutorFeedback) {
        key = 'tutor-feedback';
      } else if (a.evaluation_type === EvaluationType.AreaOfLearning && a.area_of_learning) {
        key = a.area_of_learning.id;
      }
      if (!key) return acc;

      if (!acc[key]) acc[key] = [];
      acc[key].push(a);

      return acc;
    }, {});
  }, [assessments]);

  const renderAssessmentLink = (assessment: AssessmentForReport) => {
    return (
      <Stack
        key={assessment.id}
        direction="row"
        alignItems="center"
        component={Link}
        to={`/assessments/${assessment.id}`}
        sx={{
          '&:hover': {
            '.MuiTypography-root, .MuiIcon-root': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          },
        }}
      >
        <Icon fontSize="small" sx={{ color: 'common.grey' }}>
          <ArrowAngleIcon />
        </Icon>
        <Typography color="common.grey2">{assessment.name}</Typography>
      </Stack>
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const tooltipTitle = useMemo(() => {
    return (
      <>
        {Object.entries(assessmentsMap).map(([key, assessments]) => (
          <React.Fragment key={key}>
            <Typography color="common.grey2">
              {getAssessmentEvaluationTitle(assessments[0])}
            </Typography>
            {assessments.map(renderAssessmentLink)}
          </React.Fragment>
        ))}
      </>
    );
  }, [assessmentsMap, getAssessmentEvaluationTitle]);

  if (!assessments.length) {
    return (
      <Typography color="common.grey" className="hoverTextSecondary">
        {'-'}
      </Typography>
    );
  }

  return (
    <Tooltip {...COMMON_TOOLTIP_PROPS} title={tooltipTitle}>
      <Typography color="common.grey2" noWrap>
        {Object.values(assessmentsMap).map(
          (a, i, arr) => getAssessmentEvaluationTitle(a[0]) + (i < arr.length - 1 ? ', ' : ''),
        )}
      </Typography>
    </Tooltip>
  );
};
